import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  makeStyles,
  Grid,
  Button,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";

import { Controller, useForm } from "react-hook-form";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { Error } from "../../../common/Error";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addCaseNote } from "../../../../redux/actions";
import {
  PublicEnquiryModel,
  PublicEnquiryResponse,
} from "../../../../domain/PublicEnquiry";
import { segmentList } from "@ofqual/portal-core/core/utils/listHelpers";
import { formatAsTodayYesterdayOrDate } from "@ofqual/portal-core/core/utils/dateUtils";
import {
  Activity,
  ActivityFeedItem,
  ActivityFeedItemDefinition,
  ActivitySegmentHeader,
} from "@ofqual/portal-activity-feed";
import { buildDefinitions } from "../buildDefinitions";
import { feedSelector } from "../../../../redux/selectors";
import { ActivityType } from "../../../../domain/ActivityType";
import { Select } from "@ofqual/portal-components/components/Select";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { caseTypeSlaService } from "../../../../services";
import { TextField } from "@ofqual/portal-components/components/TextField";

const useStyles = makeStyles((_) => ({
  actions: {
    padding: 15,
  },
}));

export const AddNoteDialog: React.FC<{
  onClose: () => void;
  resource: PublicEnquiryModel;
}> = ({ onClose, resource }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isResponseCitizen, setIsResponseCitizen] = useState<boolean>(false);
  const [citizenValidation, setCitizenValidation] = useState<boolean>(false);

  const { handleSubmit, control, errors } = useForm({
    defaultValues: {
      message: null,
      messageFiles: [],
      caseNote: null,
      subject: null
    },
  });

  const [caseTypeSlaLookups] = useLookup(caseTypeSlaService);

  const handleAddNote = (data: {
    caseFiles: File[];
    caseNote: string;
    sla_ResponseType: number;
    subject: string;
  }) => {
    //console.log("handleAddNote", data);
    dispatch(
      addCaseNote(
        resource.id,
        data.caseFiles,
        data.caseNote,
        data.sla_ResponseType,
        data.subject
      )
    );
    onClose();
  };

  const definitions: ActivityFeedItemDefinition<any>[] = buildDefinitions(
    () => { },
    resource,
    caseTypeSlaLookups
  );

  const Values = (): Activity[] => {
    const activity = useSelector(feedSelector);
    const value = activity[resource.id];
    const activityTypes = [
      ActivityType.PublicEnqiryCreateNote,
      ActivityType.PublicEnquiryInternalMessage,
      ActivityType.PublicEnquiryPublicMessage
    ];
    const items = value.response.data
      .filter(
        (item) => activityTypes.includes(item.activityType)
      )
      .sort((a, b) => {
        let dateA = new Date(a.createdDate);
        let dateB = new Date(b.createdDate);
        return dateB.getTime() - dateA.getTime();
      });

    return items;
  };

  const segmentedActivities = segmentList(
    Values(),
    (item) => formatAsTodayYesterdayOrDate(item.createdDate) || ""
  );

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle data-test="activity-title">Add a note</DialogTitle>
      <Divider />
      <DialogContent style={{ minHeight: "250px" }}>
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="sla_ResponseType"
                render={({ onChange, value }) => (
                  <Select
                    label="Note Type"
                    data-test="notes-case-type-field"
                    onChange={(e) => {
                      if (
                        ((e.target.value as unknown) as number) ===
                        PublicEnquiryResponse.ResponseCustomer
                      ) {
                        setCitizenValidation(true);
                        setIsResponseCitizen(true);
                      } else {
                        setCitizenValidation(false);
                        setIsResponseCitizen(false);
                      }
                      onChange(e.target.value);
                    }}
                    value={value}
                  >
                    {caseTypeSlaLookups}
                  </Select>
                )}
              />
              {isResponseCitizen && (
                <Controller
                  control={control}
                  rules={{ required: isResponseCitizen }}
                  name="citizenValidation"
                  render={({ onChange }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          data-test="citizen-response-checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              if (citizenValidation) {
                                setCitizenValidation(false);
                              }
                            } else {
                              if (!citizenValidation) {
                                setCitizenValidation(true);
                              }
                            }
                            onChange(e.target.checked);
                          }}
                        />
                      }
                      label="I understand that this communication is directed at, and visible to, the citizen *"
                      labelPlacement="start"
                      style={{ color: citizenValidation ? "red" : undefined }}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="subject"
                rules={{ required: true }}
                render={({ onChange, value }) => (
                  <TextField
                    onChange={onChange}
                    value={value}
                    data-test="case-note-subject-field"
                    label="Subject"
                    required
                  />
                )}
                required
              />
              {errors.subject && (
                <Error data-test="case-note-subject-field-error">
                  This field is required
                </Error>
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="caseNote"
                rules={{ required: true }}
                render={({ onChange, value }) => (
                  <MarkdownEditor
                    onChange={onChange}
                    value={value}
                    dataTest="create-note-text-field"
                  />
                )}
              />
              {errors.caseNote && (
                <Error data-test="create-note-text-field-error">
                  This field is required
                </Error>
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="caseFiles"
                as={
                  <FileInput
                    multiple={true}
                    dataTest="create-note-add-attachment-button"
                  />
                }
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          color="primary"
          data-test="close-add-note-dialog"
        >
          Close
        </Button>
        <Button
          onClick={handleSubmit(handleAddNote)}
          color="primary"
          data-test="add-note-message-button"
        >
          Add
        </Button>
      </DialogActions>
      <Divider />
      <DialogContent>
        {segmentedActivities.map((segment) => (
          <div key={segment.label}>
            <ActivitySegmentHeader label={segment.label} />
            {segment.data.map((item) => (
              <ActivityFeedItem
                key={item.id}
                activity={item}
                definition={definitions.find(
                  (def) => def.activityType === item.activityType
                )}
              />
            ))}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
};
